import React from 'react'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { themeConstants } from '@/styles/theme-constants.ts'
import { RankCardColors } from './ranking-card.tsx'
import { ReturnPill } from '@/common/components/return-pill.tsx'
import { RankMedal } from './ranking-podium.tsx'
import {
  PODIUM_BORDER_COLOR,
  RankCardData,
  maskedTextStyle,
  inactiveIdentityStyle,
  pnlColor,
} from './ranking-card.types.ts'

// A single highlight entry with its metrics pre-formatted by the container.
export interface HighlightItem {
  data: RankCardData
  returnPctText: string
  cumulativePnlText: string
  dailyPnlText: string
}

interface RankingHighlightProps {
  items: HighlightItem[]
  colors: RankCardColors
  dailyLabel: string
}

// Desktop top-3 "Leaderboard Highlight" strip: three side-by-side cards, each
// with a rank medal, identity + score, cumulative PnL, a return-% pill and the
// daily PnL. Reuses the mobile card view model and medal assets.
export const RankingHighlight: React.FC<RankingHighlightProps> = React.memo(
  ({ items, colors, dailyLabel }) => (
    <div
      style={{
        display: 'flex',
        gap: themeConstants.marginBase,
        alignItems: 'stretch',
        flexWrap: 'wrap',
      }}
    >
      {items.map((item) => (
        <HighlightCard
          key={item.data.key}
          item={item}
          colors={colors}
          dailyLabel={dailyLabel}
        />
      ))}
    </div>
  )
)

interface HighlightCardProps {
  item: HighlightItem
  colors: RankCardColors
  dailyLabel: string
}

const identityEllipsisStyle: React.CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const HighlightCard: React.FC<HighlightCardProps> = React.memo(
  ({ item, colors, dailyLabel }) => {
    const { data } = item
    const rank = data.rank ?? 0
    const borderColor = PODIUM_BORDER_COLOR[rank] ?? colors.listCardOutline

    return (
      <div
        style={{
          flex: '1 1 320px',
          minWidth: 0,
          display: 'flex',
          alignItems: 'flex-start',
          gap: themeConstants.marginBase,
          padding: `${themeConstants.marginXS} ${themeConstants.marginBase}`,
          borderRadius: themeConstants.borderRadiusLG,
          border: `1px solid ${borderColor ?? 'transparent'}`,
          backgroundColor: colors.cardBg,
          backdropFilter: 'blur(4px)',
        }}
      >
        <div style={{ width: 48, height: 48, flexShrink: 0 }}>
          <RankMedal rank={rank} />
        </div>

        <div
          style={{
            flex: '1 0 0',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: themeConstants.marginXXS,
          }}
        >
          <HighlightIdentity data={data} linkColor={colors.linkColor} />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: themeConstants.marginXS,
              minWidth: 0,
            }}
          >
            <IdeyaTypography.Text
              size={'xl'}
              textStyle={'strong'}
              style={{
                color: pnlColor(data.cumulativePnl, colors),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {item.cumulativePnlText}
            </IdeyaTypography.Text>
            <ReturnPill
              value={item.data.returnPct}
              text={item.returnPctText}
              colors={colors}
            />
          </div>

          <IdeyaTypography.Text
            size={'sm'}
            textStyle={'strong'}
            style={{
              color: colors.tertiaryTextColor,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {`${item.dailyPnlText} ${dailyLabel}`}
          </IdeyaTypography.Text>
        </div>
      </div>
    )
  }
)

interface HighlightIdentityProps {
  data: RankCardData
  linkColor?: string
}

// Identity row: masked rows show `*****`, everything else shows the name plus
// an optional (★score). Openable rows render the name as an idea-list link.
const HighlightIdentity: React.FC<HighlightIdentityProps> = ({
  data,
  linkColor,
}) => {
  if (data.isMasked) {
    return (
      <IdeyaTypography.Text
        size={'sm'}
        type={'secondary'}
        style={identityEllipsisStyle}
      >
        <span style={maskedTextStyle}>*****</span>
      </IdeyaTypography.Text>
    )
  }

  const nameNode = data.href ? (
    <a
      href={data.href}
      target={'_blank'}
      rel={'noopener noreferrer'}
      title={data.identity}
      style={{
        color: linkColor,
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
    >
      {data.identity}
    </a>
  ) : data.isInactive ? (
    <span style={inactiveIdentityStyle}>{data.identity}</span>
  ) : (
    data.identity
  )

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        alignItems: 'baseline',
        minWidth: 0,
      }}
    >
      <IdeyaTypography.Text
        size={'sm'}
        type={'secondary'}
        style={identityEllipsisStyle}
      >
        {nameNode}
      </IdeyaTypography.Text>
      {data.score != null && (
        <IdeyaTypography.Text
          size={'sm'}
          type={'secondary'}
          style={{ flexShrink: 0 }}
        >
          (★{data.score})
        </IdeyaTypography.Text>
      )}
    </div>
  )
}
